import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Lock, RotateCcw } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { LOCKED_MODULES, MODULE_REGISTRY, useModuleSettings } from '../context/ModuleSettingsContext';
import { useConstruction } from '../context/ConstructionContext';
import { renewableStageLabels, renewableStages } from '../data/renewablesZero';
import Button from './common/Button';
import { DemoButton } from './construction/ConstructionShell';
import { t } from '../i18n';

const Toggle = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${checked ? 'bg-brand-600' : 'bg-neutral-300'}`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </button>
);

// Administración → Configuración de módulos (equivale a la tabla module_settings).
const ModuleSettingsPanel = () => {
  const { currentUser, addAuditRecord } = useAppContext();
  const { settings, isModuleActive, setModuleActive, moveModule, resetModuleSettings, canManageModules } = useModuleSettings();
  const { settings: constructionSettings, updateSettings } = useConstruction();
  const [error, setError] = useState('');
  if (!canManageModules(currentUser)) return null;

  const ordered = [...MODULE_REGISTRY].sort((a, b) => (settings.modules[a.key]?.order ?? a.order) - (settings.modules[b.key]?.order ?? b.order));

  const toggle = (key, label, value) => {
    setModuleActive(key, value);
    addAuditRecord(value ? 'Módulo activado' : 'Módulo desactivado', key, label);
  };

  return (
    <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{t('Configuración de módulos')}</h3>
          <p className="text-xs text-slate-500">{t('Activa o desactiva módulos y submódulos. Un módulo desactivado no aparece en el menú y su ruta queda bloqueada para todos los usuarios.')}</p>
        </div>
        <Button
          size="sm"
          icon={RotateCcw}
          onClick={() => {
            if (window.confirm(t('¿Restablecer todos los módulos a sus valores por defecto?'))) {
              resetModuleSettings();
              addAuditRecord('Módulos restablecidos', 'module_settings', 'default');
            }
          }}
        >
          {t('Restablecer')}
        </Button>
      </div>

      <div className="border border-neutral-200 rounded divide-y divide-neutral-100">
        {ordered.map((module, index) => {
          const active = isModuleActive(module.key);
          const locked = LOCKED_MODULES.has(module.key);
          return (
            <div key={module.key} className="px-4 py-3">
              <div className="flex items-center gap-3">
                <Toggle checked={active} disabled={locked} onChange={(value) => toggle(module.key, module.label, value)} label={t(module.label)} />
                <span className={`text-sm font-medium ${active ? 'text-neutral-900' : 'text-neutral-400'}`}>{t(module.label)}</span>
                {locked && <Lock className="w-3.5 h-3.5 text-neutral-400" title={t('Módulo base: no se puede desactivar')} />}
                <span className="text-[11px] text-neutral-400">{t('Alcance: global')}</span>
                <div className="ml-auto flex gap-1">
                  <button type="button" className="p-1 text-neutral-400 hover:text-neutral-800 disabled:opacity-30" disabled={index === 0} onClick={() => moveModule(module.key, 'up')} title={t('Subir en el menú')}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" className="p-1 text-neutral-400 hover:text-neutral-800 disabled:opacity-30" disabled={index === ordered.length - 1} onClick={() => moveModule(module.key, 'down')} title={t('Bajar en el menú')}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {module.children.length > 0 && (
                <div className="mt-2 ml-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                  {module.children.map((child) => {
                    const childActive = isModuleActive(child.key);
                    const childLocked = LOCKED_MODULES.has(child.key);
                    return (
                      <label key={child.key} className="flex items-center gap-2 text-xs">
                        <Toggle checked={childActive} disabled={childLocked || !active} onChange={(value) => toggle(child.key, child.label, value)} label={t(child.label)} />
                        <span className={childActive && active ? 'text-neutral-700' : 'text-neutral-400'}>{t(child.label)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isModuleActive('construction-module') && (
        <div className="border border-neutral-200 rounded p-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-neutral-900">{t('Parámetros de Construcción')}</h4>
            <p className="text-xs text-neutral-500">{t('Umbral del gate de activación y tolerancia para alertas de consumo de materiales.')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <label className="text-xs text-neutral-600">
              {t('Habilitar Construcción desde la etapa')}
              <select
                className="mt-1 w-full h-8 px-2.5 text-sm rounded border border-neutral-300 bg-white"
                value={constructionSettings.activationStage}
                onChange={(event) => {
                  try { setError(''); updateSettings({ activationStage: event.target.value }); } catch (updateError) { setError(updateError.message); }
                }}
              >
                {renewableStages.filter((stage) => ['rtb', 'ntp'].includes(stage)).map((stage) => (
                  <option key={stage} value={stage}>{renewableStageLabels[stage]}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-neutral-600">
              {t('Tolerancia de consumo de materiales (%)')}
              <input
                type="number"
                min="1"
                max="50"
                className="mt-1 w-full h-8 px-2.5 text-sm rounded border border-neutral-300"
                value={constructionSettings.materialTolerancePct}
                onChange={(event) => {
                  try { setError(''); updateSettings({ materialTolerancePct: Math.max(1, Number(event.target.value) || 10) }); } catch (updateError) { setError(updateError.message); }
                }}
              />
            </label>
            <div className="flex items-center gap-2">
              <DemoButton />
            </div>
          </div>
          {error && <p className="text-xs text-danger-700">{t(error)}</p>}
        </div>
      )}
    </div>
  );
};

export default ModuleSettingsPanel;
